import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatureKey } from 'src/app/shared/enums';
import { AppSecurityService } from 'src/app/shared/services';
import { AssistantService, IAssistantMessage, IAssistantStatus } from 'src/app/shared/services/assistant.service';

/**
 * The AI billing assistant: a floating launcher pinned to the BOTTOM LEFT of every page (app shell)
 * that opens a slide-up chat panel. Answers billing questions through the assistant API (Claude +
 * in-process billing tools); the conversation lives in AssistantService so it survives navigation.
 *
 * The Autonomous QA toggle only renders when the signed-in role holds the (unadvertised)
 * ai-assistant-auto-qa feature AND the server confirms it - and the server re-checks the role on
 * every turn regardless of what the request claims.
 */
@Component({
    selector: 'app-assistant-chat',
    standalone: true,
    imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatSlideToggleModule, MatTooltipModule],
    templateUrl: './assistant-chat.component.html',
    styleUrls: ['./assistant-chat.component.scss']
})
export class AssistantChatComponent implements OnInit, AfterViewChecked {

    public open = false;
    /** Minimized = the conversation stays alive but the panel collapses to a small pill, so
     *  Bill stops covering the page while the admin works. Close (the X) still fully closes. */
    public minimized = false;
    public sending = false;
    public draft = '';
    public status: IAssistantStatus | null = null;
    public autoQa = false;

    @ViewChild('messageList') private messageList?: ElementRef<HTMLDivElement>;
    private stickToBottom = false;

    constructor(
        public assistant: AssistantService,
        private appSecurity: AppSecurityService) { }

    public ngOnInit(): void {
        this.autoQa = this.assistant.autoQa;
    }

    public ngAfterViewChecked(): void {
        if (this.stickToBottom && this.messageList) {
            this.messageList.nativeElement.scrollTop = this.messageList.nativeElement.scrollHeight;
            this.stickToBottom = false;
        }
    }

    public get messages(): IAssistantMessage[] {
        return this.assistant.messages;
    }

    /** The toggle shows only when the role holds the auto-QA feature (server confirms too). */
    public get showAutoQaToggle(): boolean {
        return this.appSecurity.hasFeature(FeatureKey.AiAssistantAutoQa) && this.status?.autoQaAvailable === true;
    }

    public toggle(): void {
        this.open = !this.open;
        this.minimized = false;
        if (this.open) {
            this.stickToBottom = true;
            if (!this.status) {
                this.assistant.status().subscribe({
                    next: s => this.status = s,
                    error: () => this.status = { configured: false, autoQaAvailable: false, model: '' }
                });
            }
        }
    }

    public minimize(): void {
        this.minimized = true;
    }

    public restore(): void {
        this.minimized = false;
        this.stickToBottom = true;
    }

    public onAutoQaChange(value: boolean): void {
        this.autoQa = value;
        this.assistant.autoQa = value;
    }

    public onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.send();
        }
    }

    public send(): void {
        const text = this.draft.trim();
        if (!text || this.sending || this.status?.configured === false) {
            return;
        }

        this.assistant.messages.push({ role: 'user', content: text });
        this.draft = '';
        this.sending = true;
        this.stickToBottom = true;

        this.assistant.chat(this.assistant.messages, this.autoQa).subscribe({
            next: response => {
                this.assistant.messages.push({
                    role: 'assistant',
                    content: response.reply,
                    actions: response.actions?.length ? response.actions : undefined
                });
                // Let open screens (e.g. the QA grid) live-refresh the data the assistant touched.
                this.assistant.notifyActions(response.actions);
                this.sending = false;
                this.stickToBottom = true;
            },
            error: () => {
                this.assistant.messages.push({
                    role: 'assistant',
                    content: 'Something went wrong sending that message. Please try again.'
                });
                this.sending = false;
                this.stickToBottom = true;
            }
        });
    }

    public clear(): void {
        this.assistant.clearConversation();
    }
}
